import { AgGridReact, AgGridReactProps } from 'ag-grid-react'
import { StateUpdatedEvent } from 'ag-grid-enterprise'
import React, { forwardRef, useCallback, useMemo } from 'react'
import agGridTheme from '../../../../config/agGrid/theme'
import '../../../../config/agGrid/agGridOverrides.css'
import { useAgGridLocale } from '../../../hooks/useAgGridLocale'
import { useGridStatePersistence } from '../../../hooks/useGridStatePersistence'
import CustomLoadingOverlay from './CustomLoadingOverlay'
import CustomHeaderTooltip from './CustomHeaderTooltip'
import { customColumnTypes } from './columnTypes'
import { useClipboardProcessors } from './useClipboardProcessor'
import './cellEditors.css'

interface CustomAgGridProps extends AgGridReactProps {
  /**
   * Opt in to persisting this table's column layout and sort to localStorage.
   * Must be unique per table across the app. Omit for no persistence.
   */
  persistStateId?: string
}

const CustomAgGrid = forwardRef<AgGridReact, CustomAgGridProps>(({ persistStateId, ...props }, ref) => {
  const localeText = useAgGridLocale()
  const { processCellForClipboard, processCellFromClipboard, processDataFromClipboard } = useClipboardProcessors()
  const { initialState: persistedState, onStateUpdated: persistState } = useGridStatePersistence(persistStateId)

  const defaultColDef = useMemo(
    () => ({
      tooltipComponent: CustomHeaderTooltip,
      ...props.defaultColDef
    }),
    [props.defaultColDef]
  )

  const defaultColGroupDef = useMemo(
    () => ({
      tooltipComponent: CustomHeaderTooltip,
      ...props.defaultColGroupDef
    }),
    [props.defaultColGroupDef]
  )

  // Persisted column/sort state wins over any caller-provided initialState keys.
  const initialState = useMemo(
    () => (persistedState ? { ...props.initialState, ...persistedState } : props.initialState),
    [persistedState, props.initialState]
  )

  const callerOnStateUpdated = props.onStateUpdated
  const handleStateUpdated = useCallback(
    (event: StateUpdatedEvent) => {
      persistState(event)
      callerOnStateUpdated?.(event)
    },
    [persistState, callerOnStateUpdated]
  )

  return (
    <AgGridReact
      ref={ref}
      theme={props.theme ?? agGridTheme}
      localeText={localeText}
      rowHeight={24}
      loadingOverlayComponent={CustomLoadingOverlay}
      columnTypes={customColumnTypes}
      enableFilterHandlers
      processCellForClipboard={processCellForClipboard}
      processCellFromClipboard={processCellFromClipboard}
      processDataFromClipboard={processDataFromClipboard}
      tooltipShowDelay={300}
      {...props}
      defaultColDef={defaultColDef}
      defaultColGroupDef={defaultColGroupDef}
      initialState={initialState}
      onStateUpdated={persistStateId ? handleStateUpdated : callerOnStateUpdated}
    />
  )
})

CustomAgGrid.displayName = 'CustomAgGrid'

export default CustomAgGrid
